import { useCapabilityRegistryContext } from '@mobile-mapping/providers';
import { CustomInput } from '@tcweb/connect-ui';
import { t } from '@trimble/i18n';
import { LineDetectionCapability } from '@trimble/web3d-capabilities';
import { Component, Show } from 'solid-js';

/* eslint-disable no-magic-numbers -- degree/radian conversion and UI bounds */
const DEG_PER_RAD = 180 / Math.PI;
const RAD_PER_DEG = Math.PI / 180;
const ANGLE_DEG_ROUND_FACTOR = 10;

const FIELD_BOUNDS = {
  maxIterations: { min: 1, max: 200 },
  spaceBetweenPoints: { min: 0.01, max: 5 },
  maxIntersectionDistance: { min: 0.001, max: 1 },
  pickVolumeSize: { min: 0.01, max: 5 },
  maxAngleDeg: { min: 1, max: 180 },
} as const;
/* eslint-enable no-magic-numbers */

type NumericSettingKey = keyof typeof FIELD_BOUNDS;

const parseClampedNumber = (
  raw: string,
  min: number,
  max: number
): number | undefined => {
  const value = Number(raw);
  if (!Number.isFinite(value)) {
    return undefined;
  }
  return Math.min(max, Math.max(min, value));
};

/**
 * Floating panel (Web3D feature-extraction demo style) for tuning line detection
 * while the tool is active.
 */
export const LineDetectionSettingsPanel: Component = () => {
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const capability = (): LineDetectionCapability | undefined =>
    capabilityRegistry.get(LineDetectionCapability)();

  const commitNumericSetting =
    (key: Exclude<NumericSettingKey, 'maxAngleDeg'>) =>
    (e: Event): void => {
      const cap = capability();
      if (!cap) {
        return;
      }
      const { min, max } = FIELD_BOUNDS[key];
      const value = parseClampedNumber(
        (e.target as HTMLInputElement).value,
        min,
        max
      );
      if (value === undefined) {
        return;
      }
      cap.updateSettings({ [key]: value });
    };

  const commitMaxAngleDeg = (e: Event): void => {
    const cap = capability();
    if (!cap) {
      return;
    }
    const degrees = parseClampedNumber(
      (e.target as HTMLInputElement).value,
      FIELD_BOUNDS.maxAngleDeg.min,
      FIELD_BOUNDS.maxAngleDeg.max
    );
    if (degrees === undefined) {
      return;
    }
    cap.updateSettings({ maxAngle: degrees * RAD_PER_DEG });
  };

  return (
    <Show when={capability()?.isActive() ? capability() : undefined} keyed>
      {(cap) => (
        <div
          data-testid="line-detection-settings-panel"
          class="pointer-events-auto absolute right-4 top-4 z-dropdown w-[22rem] rounded border border-gray-2 bg-white p-3 shadow-md dark:border-gray-7 dark:bg-gray-9"
        >
          <div class="mb-3 flex items-center justify-between gap-2">
            <span class="text-sm font-semibold">
              {t('mobileMapping.lineDetection.settings.title')}
            </span>
            <modus-wc-button
              aria-label={t('mobileMapping.lineDetection.settings.reset')}
              color="secondary"
              shape="rectangle"
              type="button"
              variant="outlined"
              onClick={() => cap.resetSettings()}
            >
              {t('mobileMapping.lineDetection.settings.reset')}
            </modus-wc-button>
          </div>

          <div class="flex flex-col gap-3">
            <CustomInput
              name="line-detection-max-iterations"
              label={t('mobileMapping.lineDetection.settings.maxIterations')}
              type="number"
              min={FIELD_BOUNDS.maxIterations.min}
              max={FIELD_BOUNDS.maxIterations.max}
              step="1"
              value={String(cap.settings().maxIterations)}
              onChange={commitNumericSetting('maxIterations')}
            />
            <CustomInput
              name="line-detection-space-between-points"
              label={t(
                'mobileMapping.lineDetection.settings.spaceBetweenPoints'
              )}
              type="number"
              min={FIELD_BOUNDS.spaceBetweenPoints.min}
              max={FIELD_BOUNDS.spaceBetweenPoints.max}
              step="0.1"
              value={String(cap.settings().spaceBetweenPoints)}
              onChange={commitNumericSetting('spaceBetweenPoints')}
            />
            <CustomInput
              name="line-detection-max-intersection-distance"
              label={t(
                'mobileMapping.lineDetection.settings.maxIntersectionDistance'
              )}
              type="number"
              min={FIELD_BOUNDS.maxIntersectionDistance.min}
              max={FIELD_BOUNDS.maxIntersectionDistance.max}
              step="0.01"
              value={String(cap.settings().maxIntersectionDistance)}
              onChange={commitNumericSetting('maxIntersectionDistance')}
            />
            <CustomInput
              name="line-detection-pick-volume-size"
              label={t('mobileMapping.lineDetection.settings.pickVolumeSize')}
              type="number"
              min={FIELD_BOUNDS.pickVolumeSize.min}
              max={FIELD_BOUNDS.pickVolumeSize.max}
              step="0.1"
              value={String(cap.settings().pickVolumeSize)}
              onChange={commitNumericSetting('pickVolumeSize')}
            />
            <CustomInput
              name="line-detection-max-angle-deg"
              label={t('mobileMapping.lineDetection.settings.maxAngleDeg')}
              type="number"
              min={FIELD_BOUNDS.maxAngleDeg.min}
              max={FIELD_BOUNDS.maxAngleDeg.max}
              step="0.1"
              value={String(
                Math.round(
                  cap.settings().maxAngle * DEG_PER_RAD * ANGLE_DEG_ROUND_FACTOR
                ) / ANGLE_DEG_ROUND_FACTOR
              )}
              onChange={commitMaxAngleDeg}
            />
          </div>
        </div>
      )}
    </Show>
  );
};
